import {Box} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import type {Printout} from "crqlar-react-common/dist/models/printout/printout.model";
import {PrintoutService} from "crqlar-react-common/dist/services/printout/printout.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import {LanguagesElement} from "../../common/uiElements/LanguagesElement";
import SettingsListPage from "../../system/layout/SettingsListPage";
import PrintoutCreateDialog from "./PrintoutCreateDialog";
import {getPrintoutLanguages} from "../core/printoutDocument";

// The new identity replaces the former Orientation-visible persisted column defaults once.
const PRINTOUTS_TABLE_ID = 'system-printouts-v2';

const PrintoutsList = () => {
    useTrackScreenVisit('system printouts');

    const navigate = useNavigate();
    const {t} = useTranslation();

    const [createOpen, setCreateOpen] = useState(false);
    const sectors = useAllSectors();

    const columns = useMemo<ColumnDef<Printout>[]>(() => {
        const sectorNamesById = new Map(sectors.flatMap(sector => (
            sector.id && sector.name ? [[sector.id, sector.name] as const] : []
        )));
        const getSectorNames = (printout: Printout) => {
            if (!printout.scopedSectorIds?.length) return t('All sectors');

            return printout.scopedSectorIds
                .map(sectorId => sectorNamesById.get(sectorId))
                .filter((name): name is string => !!name)
                .join(', ');
        };

        return [{
            id: 'name',
            header: t('Name'),
            accessorFn: printout => printout.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 280,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'type',
            header: t('Type'),
            accessorFn: printout => printout.type,
            cell: ({row}) => <Box>{row.original.type ? t(`Printout type ${row.original.type}`) : ''}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'sectors',
            header: t('Sectors'),
            accessorFn: getSectorNames,
            cell: ({getValue}) => <Box>{getValue<string>()}</Box>,
            size: 240,
            meta: {defaultActive: true}
        },
        {
            id: 'orientation',
            header: t('Orientation'),
            accessorFn: printout => printout.orientation,
            cell: ({row}) => <Box>{row.original.orientation ? t(`Printout orientation ${row.original.orientation}`) : ''}</Box>,
            size: 160,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'languages',
            header: t('Languages'),
            accessorFn: printout => getPrintoutLanguages(printout).join(', '),
            cell: ({row}) => <LanguagesElement languageCodes={getPrintoutLanguages(row.original)}/>,
            size: 150,
            meta: {defaultActive: true, isServerSortable: false}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: printout => printout.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: printout => printout.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }];
    }, [sectors, t]);

    const controller = useServerTable({
        tableId: PRINTOUTS_TABLE_ID,
        columns,
        getRowId: printout => printout.id!,
        onRowClick: printout => navigate(`/system/printouts/${printout.id}`),
        initialSorting: [{id: 'name', desc: false}],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await PrintoutService.getAllPrintouts({
                name: searchQuery ? [searchQuery] : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });
            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        }
    });

    return (
        <SettingsListPage title={t('Printouts (new)')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Create printout')}
                            onClick={() => setCreateOpen(true)}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>

            {createOpen && (
                <PrintoutCreateDialog
                    open
                    onClose={() => setCreateOpen(false)}
                />
            )}
        </SettingsListPage>
    );
};

export default PrintoutsList;
